'use client'

import React, { useState, useCallback } from 'react'
import { Button } from '@/shared/components/ui/button'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { MessageCircle, AlertCircle, Download } from 'lucide-react'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import { format } from 'date-fns'
import { toast } from 'sonner'

function escapeCsvCell(val: unknown): string {
    if (val === null || val === undefined) return ''
    const s = String(val)
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`
    }
    return s
}

function PlanningAiChatTab() {
    const [question, setQuestion] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [rows, setRows] = useState<Record<string, unknown>[]>([])
    const [columns, setColumns] = useState<string[]>([])
    const { regionId } = useRegionId()

    const handleDownloadCsv = useCallback(() => {
        if (rows.length === 0 || columns.length === 0) {
            toast.error('No results to download')
            return
        }
        try {
            const header = columns.map(escapeCsvCell).join(',')
            const body = rows.map((row) =>
                columns.map((col) => escapeCsvCell(row[col])).join(',')
            )
            const csv = [header, ...body].join('\n')
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `ai-chat-results-${format(new Date(), 'yyyy-MM-dd')}.csv`
            a.click()
            URL.revokeObjectURL(url)
            toast.success(`Downloaded ${rows.length} row${rows.length !== 1 ? 's' : ''}`)
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Download failed')
        }
    }, [rows, columns])

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            if (!question.trim()) return

            setIsLoading(true)
            setError(null)
            setRows([])
            setColumns([])

            try {
                const res = await authFetchJson<any>('/ai/chat/query', {
                    method: 'POST',
                    body: JSON.stringify({
                        question: question.trim(),
                        regionId: regionId ?? undefined,
                    }),
                })

                if (!res.success) {
                    setError(res.message || 'Failed to process question')
                    return
                }

                const payload = res.data ?? res
                const resultRows = Array.isArray(payload.rows) ? payload.rows : []
                const resultColumns = Array.isArray(payload.columns) ? payload.columns : []

                setRows(resultRows)
                setColumns(resultColumns.length > 0 ? resultColumns : (resultRows[0] ? Object.keys(resultRows[0] as object) : []))
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred')
            } finally {
                setIsLoading(false)
            }
        },
        [question, regionId]
    )

    return (
        <div className="flex flex-col gap-6 w-full">
            <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-sm text-muted-foreground mb-4">
                    Ask questions about homeowners, volunteers, work orders, and other data. 
                    Results are read-only. Example: &quot;How many active work orders are there in this region?&quot;
                </p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                    <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="e.g., List homeowners with their addresses and phone numbers"
                        className="min-h-[100px] w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        disabled={isLoading}
                        maxLength={4000}
                    />
                    <div className="flex items-center gap-2">
                        <Button
                            type="submit"
                            disabled={isLoading || !question.trim()}
                            showLoader={isLoading}
                            loadingText="Processing..."
                        >
                            <MessageCircle className="mr-2 h-4 w-4" />
                            Ask
                        </Button>
                        {question.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                                {question.length}/4000
                            </span>
                        )}
                    </div>
                </form>
            </div>

            {error && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <p className="text-sm">{error}</p>
                </div>
            )}

            {rows.length > 0 && (
                <div className="rounded-lg border overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-4 py-2 bg-muted/30 border-b">
                        <span className="text-xs text-muted-foreground">
                            {rows.length} row{rows.length !== 1 ? 's' : ''} returned
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadCsv}
                            className="h-8 gap-2"
                        >
                            <Download className="h-4 w-4" />
                            Download CSV
                        </Button>
                    </div>
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-muted">
                                <tr>
                                    {columns.map((col) => (
                                        <th
                                            key={col}
                                            className="px-4 py-2 text-left font-medium border-b"
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, i) => (
                                    <tr key={i} className="border-b last:border-0 hover:bg-muted/50">
                                        {columns.map((col) => (
                                            <td key={col} className="px-4 py-2">
                                                {row[col] != null ? String(row[col]) : '—'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!isLoading && rows.length === 0 && !error && question && (
                <p className="text-sm text-muted-foreground">
                    No results. Try rephrasing your question.
                </p>
            )}
        </div>
    )
}

export default PlanningAiChatTab
